import { useEffect, useState, useCallback } from "react";
import { uniqBy } from "lodash";
import { Button } from "@/components/ui/Button";
import { useModalAction } from "@/contexts/modal/Context";
import { useDrag, useDrop } from "react-dnd";
import { getCorrectionResults } from "@/utils/exercises";
import { ExerciseGroupingsItem } from "@/types";
import GroupingIcon from "@/components/icons/exerciseV2/GroupingIcon";

interface WordButtonProps {
  children: React.ReactNode;
  showCorrection: boolean;
  correctionResults?: CorrectionResult[];
}

interface CorrectionResult {
  word: string;
  isCorrect: boolean;
}

interface DroppableContainerProps {
  onDrop: (name: string) => void;
  collectedItems: string[];
  label: string;
  showCorrection: boolean;
  correctionResults: CorrectionResult[];
}

interface GroupingExerciseProps {
  data: ExerciseGroupingsItem[];
}

export interface CategoryData {
  id: number;
  category: string;
  word: string;
}

interface DraggableItem {
  name: string;
}

export interface SubmissionDataItem {
  questionId: number;
  textAnswer: string;
  selectedOptionId?: number;
  isCorrect?: boolean;
}

interface CategoryState {
  name: string;
  words: string[];
}

const WordButton: React.FC<WordButtonProps> = ({
  children,
  showCorrection,
  correctionResults,
}) => {
  const [, drag] = useDrag(() => ({
    type: "word",
    item: { name: children },
  }));

  const isCorrect = correctionResults?.find(
    (result) => result.word === children,
  )?.isCorrect;
  const backgroundColorClass = !showCorrection
    ? "bg-brand-secondary"
    : isCorrect
    ? "bg-[#219653]"
    : "bg-[#C80505]";

  return (
    <div
      ref={drag}
      className={`justify-center px-3 py-1 ${backgroundColorClass} text-sm text-white rounded-md cursor-grab`}
    >
      {children}
    </div>
  );
};

const DroppableContainer: React.FC<DroppableContainerProps> = ({
  label,
  onDrop,
  collectedItems,
  showCorrection,
  correctionResults,
}) => {
  const [, drop] = useDrop({
    accept: "word",
    drop: showCorrection
      ? undefined
      : (item) => {
          onDrop((item as DraggableItem).name);
        },
  });

  return (
    <div
      ref={drop}
      className="flex flex-col grow pb-20 w-full text-sm leading-4 text-center text-white whitespace-nowrap bg-white rounded-md border border-brand-secondary border-solid"
    >
      <h2 className="justify-center items-center px-16 py-2 bg-brand-secondary rounded-md">
        {label.toUpperCase()}
      </h2>

      <div className="flex gap-1.5 self-start mt-2.5 ml-3.5 flex-wrap">
        {collectedItems.map((item) => (
          <WordButton
            key={item}
            showCorrection={showCorrection}
            correctionResults={correctionResults}
          >
            {item}
          </WordButton>
        ))}
      </div>
    </div>
  );
};

function shuffleArray(array: string[]) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

const GroupingExercise: React.FC<GroupingExerciseProps> = ({ data }) => {
  const { openModal } = useModalAction();
  const [categories, setCategories] = useState<CategoryState[]>([]);
  const [uncategorizedWords, setUncategorizedWords] = useState<string[]>([]);
  const [showCorrection, setShowCorrection] = useState(false);
  const [correctAnswersCount, setCorrectAnswersCount] = useState(0);
  const [totalWords, setTotalWords] = useState(0);
  const [submissionData, setSubmissionData] = useState<SubmissionDataItem[]>(
    [],
  );
  const [isShuffled, setIsShuffled] = useState(false);

  useEffect(() => {
    if (data.length > 0 && !isShuffled) {
      // Group data by category
      const categoryGroups = data.reduce(
        (acc, item) => {
          if (!acc[item.category]) {
            acc[item.category] = [];
          }
          acc[item.category].push(item.word);
          return acc;
        },
        {} as Record<string, string[]>,
      );

      // Initialize categories state
      setCategories(
        Object.entries(categoryGroups).map(([name]) => ({
          name,
          words: [],
        })),
      );

      // Get all words and shuffle them
      const allWords = data.map((item) => item.word);
      setUncategorizedWords(shuffleArray([...allWords]));
      setIsShuffled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (data.length > 0) {
      const totalWordsTransformed = data.reduce((acc, currentCategoryData) => {
        const wordsTransformed = currentCategoryData.word
          .split(",")
          .map((word) => word.trim());
        return acc + wordsTransformed.length;
      }, 0);

      setTotalWords(totalWordsTransformed);
    }
  }, [data]);

  const handleDrop = (word: string, targetCategory: string) => {
    setCategories((prev) =>
      prev.map((cat) => {
        // Remove word from other categories if it exists
        if (cat.name !== targetCategory && cat.words.includes(word)) {
          return { ...cat, words: cat.words.filter((w) => w !== word) };
        }
        // Add word to target category
        if (cat.name === targetCategory && !cat.words.includes(word)) {
          return { ...cat, words: [...cat.words, word] };
        }
        return cat;
      }),
    );
    setUncategorizedWords((prev) => prev.filter((w) => w !== word));
  };

  const handleShowCorrection = () => {
    setShowCorrection(true);

    // Create a mapping of correct words for each category
    const correctWordsMapping = data.reduce<Record<string, string[]>>(
      (acc, current) => {
        if (!acc[current.category]) {
          acc[current.category] = [];
        }
        acc[current.category].push(current.word);
        return acc;
      },
      {},
    );

    // Check correctness for all categories
    let totalCorrectAnswers = 0;

    categories.forEach((category) => {
      const correctWordsForCategory = correctWordsMapping[category.name] || [];
      const correctWordsCount = category.words.filter((word) =>
        correctWordsForCategory.includes(word),
      ).length;
      totalCorrectAnswers += correctWordsCount;
    });

    setCorrectAnswersCount(totalCorrectAnswers);

    // Prepare submission data
    const newSubmissionData: SubmissionDataItem[] = data.map(
      (categoryData) => ({
        questionId: categoryData.id,
        textAnswer:
          categories
            .find((cat) => cat.name === categoryData.category)
            ?.words.join(",") || "",
        isCorrect: categories
          .find((cat) => cat.name === categoryData.category)
          ?.words.includes(categoryData.word),
      }),
    );

    setSubmissionData(newSubmissionData);
  };

  const handleShowMyScore = useCallback(() => {
    openModal(
      "EXERCISE_V2_MODAL",
      {
        exerciseType: "groupings",
        score: correctAnswersCount,
        total: totalWords,
        contentClass: "bg-brand-lavender",
        submissionData: submissionData,
      },
      {
        renderHeader: (
          <div
            className={
              "flex items-center px-2 h-10 bg-brand-secondary rounded-t-md"
            }
          >
            <p className={"text-14px text-white font-semibold"}>Grouping</p>
          </div>
        ),
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correctAnswersCount, totalWords, submissionData]);

  return (
    <div className="flex flex-col gap-8 px-5 py-5 bg-white rounded-lg border border-brand-secondary border-solid max-md:px-5">
      <header className="flex gap-5 justify-between w-full max-md:flex-wrap max-md:max-w-full">
        <div className="flex gap-5 justify-between items-center text-xl leading-6 text-brand">
          <GroupingIcon className="w-[40px] h-[32px]" />
          <div className="w-full italic">
            <p className="text-sm font-semibold ">Grouping</p>
            <p className="text-xs font-normal italic text-brand">
              Match the answers correctly
            </p>
          </div>
        </div>

        <div className="flex gap-6">
          <Button
            size="sm"
            variant="secondary"
            onClick={handleShowCorrection}
            disabled={uncategorizedWords.length > 0 || showCorrection}
            className="rounded-[10px] h-[30px] text-[14px]"
          >
            Correct now
          </Button>
        </div>
      </header>

      <div className="flex flex-col gap-2">
        {/* Uncategorized words */}
        <div className="flex flex-wrap gap-1.5 p-2.5 bg-brand-light rounded-md">
          {uniqBy(uncategorizedWords, (word) => word).map((word) => (
            <WordButton key={word} showCorrection={false}>
              {word}
            </WordButton>
          ))}
        </div>

        {/* Categories */}
        <div className="grid grid-cols-2 gap-4">
          {categories.map((category) => (
            <DroppableContainer
              key={category.name}
              onDrop={(word) => handleDrop(word, category.name)}
              collectedItems={category.words}
              label={category.name}
              showCorrection={showCorrection}
              correctionResults={getCorrectionResults(
                category.name,
                category.words,
                data,
              )}
            />
          ))}
        </div>
      </div>

      {showCorrection && (
        <Button
          size={"default"}
          variant={"default"}
          onClick={handleShowMyScore}
          className="mx-auto border-brand text-[16px]"
        >
          My Score
        </Button>
      )}
    </div>
  );
};

export default GroupingExercise;
